import React, { useEffect, useMemo, useState } from "react";
import { Button, ProfilePaymentIcon, ProfilePaymentIcons } from "@components";
import { SectionHeader, SectionTitle, SectionTagline } from "../components";
import ProfileAccountsTable from "../components/ProfileAccountsTable";
import { PlusLg } from "react-bootstrap-icons";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import SettingsMenuModal from "../modals/SettingsMenuModal";
import { Switch } from "@headlessui/react";
import {
  createPaymentProfile,
  updatePaymentProfile,
  deletePaymentProfile,
} from "@api";
import { useMerchant, usePaymentSettings } from "@auth";
import { toast } from "@components/toast/Toast";
import { usePaymentProfilesContext } from "@context/PaymentProfilesContext";
import useProfileCustomers from "../hooks/useProfileCustomers";
import ProfileDeleteWarning from "../components/ProfileDeleteWarning";
import { applyPaymentMethodRules } from "@utils";

const availableMethods = [
  {
    id: "allowOpenBanking",
    name: "Pay by Bank",
    icon: "Bank",
    secondaryTag: "(via Ontik)",
  },
  { id: "allowCards", name: "Personal / Corporate Card", icon: "Card" },
  { id: "allowAmex", name: "American Express", icon: "Amex" },
];

function PaymentProfiles() {
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [menuProfile, setMenuProfile] = useState(null);
  const { paymentSettings, reloadPaymentSettings } = usePaymentSettings();
  const { profiles: contextProfiles, refresh } = usePaymentProfilesContext();

  const profiles = useMemo(
    () =>
      [...contextProfiles].sort(
        (a, b) => (b.isSystemDefault ? 1 : 0) - (a.isSystemDefault ? 1 : 0),
      ),
    [contextProfiles],
  );

  const activeProfile = profiles.find((p) => p.id === activeProfileId) ?? null;

  const {
    accounts,
    loading: customersLoading,
    searchTerm: customerSearch,
    setSearchTerm: setCustomerSearch,
    hasMoreData,
    fetchMore,
  } = useProfileCustomers(activeProfileId);

  // When the profile list first loads (or after refresh), default the active tab
  // to the first profile if nothing is selected (or the current selection is gone).
  useEffect(() => {
    if (profiles.length === 0) return;
    if (!profiles.some((p) => p.id === activeProfileId)) {
      setActiveProfileId(profiles[0].id);
    }
  }, [profiles, activeProfileId]);

  const handleAddProfile = () => {
    setMenuProfile({ name: "", isNew: true });
  };

  const handleRenameProfile = async (item, newName) => {
    if (item.isNew) {
      const prConfig = paymentSettings?.paymentRequests ?? {};
      const created = await createPaymentProfile(
        newName,
        false,
        prConfig.allowCard ?? false,
        prConfig.allowOpenBanking ?? true,
        prConfig.allowKlarna ?? false,
        prConfig.allowAmex ?? false,
        true,
      );
      await refresh();
      if (created?.id) {
        setActiveProfileId(created.id);
      }
      toast({ description: `Created "${newName}" Payment profile` });
    } else {
      await updatePaymentProfile(
        item.id,
        newName,
        item.isSystemDefault,
        item.allowCards,
        item.allowOpenBanking,
        item.allowKlarna,
        item.allowAmex,
        item.allowBacs,
      );
      await refresh();
      toast({ description: `Renamed Payment profile to "${newName}"` });
    }
    setMenuProfile(null);
  };

  const handleDeleteProfile = async (item) => {
    await deletePaymentProfile(item.id);
    await refresh();
    if (item.id === activeProfileId) {
      setActiveProfileId(null); // useEffect will pick the next one
    }
    toast({ description: `Deleted "${item.name}" Payment profile` });
    setMenuProfile(null);
  };

  const toggleMethod = async (methodId) => {
    if (!activeProfile) return;

    const newValue = !activeProfile[methodId];
    const next = applyPaymentMethodRules(
      { ...activeProfile, [methodId]: newValue },
      methodId,
      { cardKey: "allowCards" },
    );

    await updatePaymentProfile(
      activeProfile.id,
      activeProfile.name,
      activeProfile.isSystemDefault,
      next.allowCards,
      next.allowOpenBanking,
      methodId === "allowKlarna" ? newValue : activeProfile.allowKlarna,
      next.allowAmex,
      methodId === "allowBacs" ? newValue : activeProfile.allowBacs,
    );
    await refresh();
  };

  return (
    <div className="flex flex-col items-center py-4 px-5 border-b border-zinc-200">
      <div className="py-8 w-full max-w-4xl">
        <SectionTitle>Trade accounts</SectionTitle>
        <h3 className="text-lg font-semibold text-zinc-900 mt-8">
          Payment profiles
        </h3>
        <p className="text-sm text-zinc-500 mt-1">
          A profile defines a set of payment methods trade accounts can use. All
          trade accounts use the General payment profile unless they have been
          assigned to a different one.
        </p>
        <div className="flex justify-end mt-6">
          <Button size="sm" color="link" onClick={handleAddProfile}>
            <PlusLg className="mr-1 " /> Add profile
          </Button>
        </div>
        <div className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex w-max text-md font-semibold">
            {profiles.map((profile) => {
              const isActive = activeProfileId === profile.id;
              return (
                <div
                  key={profile.id}
                  role="button"
                  onClick={() => setActiveProfileId(profile.id)}
                  className={`flex items-center justify-center text-center select-none transition-colors px-2 py-2 whitespace-nowrap group/tab border-b-2 ${
                    isActive
                      ? "text-primary border-primary"
                      : "text-zinc-900 hover:text-zinc-700 border-transparent"
                  }`}
                >
                  <span>
                    {profile.name}
                    {profile.customerCount != null && (
                      <span className="font-normal text-zinc-500 text-xs ml-1">
                        ({profile.customerCount})
                      </span>
                    )}
                  </span>
                  &nbsp;&nbsp;
                  <ProfilePaymentIcons profile={profile} size="sm" />{" "}
                  {!profile.isSystemDefault && (
                    <PencilSquareIcon
                      className="size-3.5 ml-1.5 hidden group-hover/tab:inline-block cursor-pointer text-primary-400 hover:text-primary-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuProfile(profile);
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {activeProfile && (
          <div className="mt-8">
            <div className="mt-4 rounded-3xl bg-white shadow-[4px_4px_16px_0px_rgba(0,0,0,0.16)] py-4 px-6">
              {availableMethods.map((method) => {
                const enabled = activeProfile[method.id];
                return (
                  <div
                    key={`${activeProfileId}-${method.id}`}
                    className="flex items-center justify-between py-2"
                  >
                    <div className="flex items-center gap-6">
                      <ProfilePaymentIcon paymentType={method.icon} />
                      <span className="text-sm font-medium text-zinc-900">
                        {method.name}
                      </span>
                    </div>
                    <Switch
                      checked={enabled}
                      onChange={() => toggleMethod(method.id)}
                      className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        enabled ? "bg-primary" : "bg-zinc-200"
                      }`}
                    >
                      <span className="sr-only">Toggle {method.name}</span>
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          enabled ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </Switch>
                  </div>
                );
              })}
            </div>

            <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2 py-2 mt-4 text-xs text-blue-800 ring-1 ring-inset ring-blue-200 w-full">
              <span className="flex size-3 shrink-0 items-center justify-center rounded-full bg-blue-800 text-[10px] font-bold leading-none text-white">
                i
              </span>
              Active payment links using this profile will automatically reflect
              any updates. Customers can always view your bank details and pay
              via BACS, these payments are not tracked in Ontik.
            </span>

            <ProfileAccountsTable
              accounts={accounts}
              loading={customersLoading}
              searchTerm={customerSearch}
              onSearchChange={setCustomerSearch}
              hasMoreData={hasMoreData}
              onLoadMore={fetchMore}
            />
          </div>
        )}
      </div>
      <SettingsMenuModal
        isOpen={!!menuProfile}
        onClose={() => setMenuProfile(null)}
        item={menuProfile}
        itemLabel="profile"
        initialPage={menuProfile?.isNew ? "name" : "menu"}
        onRename={handleRenameProfile}
        onDelete={
          menuProfile?.isNew || menuProfile?.isSystemDefault
            ? undefined
            : handleDeleteProfile
        }
        deleteWarning={
          menuProfile && !menuProfile.isNew && !menuProfile.isSystemDefault ? (
            <ProfileDeleteWarning
              profileId={menuProfile.id}
              defaultProfileName={profiles.find((p) => p.isSystemDefault)?.name}
            />
          ) : null
        }
        isSerious={false}
      />
    </div>
  );
}

export default PaymentProfiles;
